/* eslint-disable node/no-process-env */
import { z } from 'zod'

import { Host } from './validation'

/**
 * An environment variable can be declared as a bool by passing "true"
 * or "1"
 */
const Bool = z.string().transform((val) => val === 'true' || val === '1')

/**
 * Environment configuration schema
 *
 * @note next.js is a PITA and requires that we substitue some env variables at build time so we therefore have the process.env.NEXT_PUBLIC_* default values here
 * @todo Defaults are provided everywhere right now due to different
 *   environments we use @gf/lib in but we should really just be parsing
 *   closer to the required code
 */
const conf = z.object({
  NEXT_PUBLIC_URL: z
    .string()
    .default(process.env['NEXT_PUBLIC_URL'] ?? 'gaiafamily.com'),
  NODE_ENV: z.enum(['development', 'production', 'test']).default('production'),
  NEXT_PUBLIC_GAIA_FEATURE_QUOTE: Bool.default(
    process.env['NEXT_PUBLIC_GAIA_FEATURE_QUOTE'] ?? '0'
  ),
  GAIA_ENV: z
    .enum(['development', 'production', 'test', 'preview'])
    .default('production'),
  GAIA_VERSION: z.string().default('unknown'),
  LOG_LEVEL: z
    .enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal'])
    .default('debug'),
  AUTH_SECRET: z.string().default('unknown'),
  AUTH_TOKEN_ENCRYPTION_KEY: z.string().default('unknown'),
  AUTH_TOKEN_SIGNING_KEY: z.string().default('unknown'),
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: z
    .string()
    .default(process.env['NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA'] ?? 'unknown'),
  GAIA_EMAIL_DOMAIN: Host.default('gaia.family'),
  POSTGRES_PRIMARY_URL: z
    .string()
    .url()
    .default('postgres://user:pass@localhost:5433/db'),
  GAIA_SERVICE_GRAPHQL_HTTP_URL: z
    .string()
    .url()
    .default('http://localhost:4000/graphql'),
  TYPEFORM_SECRET: z.string().default('unknown'),
  NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID: z
    .string()
    .default(process.env['NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID'] ?? 'unknown'),
  NEXT_PUBLIC_MAPBOX_API_KEY: z
    .string()
    .default(process.env['NEXT_PUBLIC_MAPBOX_API_KEY'] ?? 'unknown'),
  GRAFANA_TEMPO_HTTP_URL: z
    .string()
    .url()
    .default('http://localhost:4318/v1/traces'),
  GRAFANA_TEMPO_ENABLED: Bool.default('0'),
  GRAFANA_TEMPO_AUTH_USERNAME: z.string().default('538021'),
  GRAFANA_TEMPO_AUTH_API_KEY: z.string().default('tempo-unknown'),
  GAIA_SERVICE: z.string().default('sandbox'),
  FEATURE_FLAG_HUBSPOT_SINGLE_SEND: z
    .string()
    .default('ckzmip23o00520dl3iyjxxjbk'),
  DECISION_CALLBACK_URL: z
    .string()
    .url()
    .default('https://risk.preview.gaiafamily.com'),
  GAIA_SECRET_BASEQL_API_KEY: z
    .string()
    .default(process.env['GAIA_SECRET_BASEQL_API_KEY'] ?? 'unknown'),
  NEXT_PUBLIC_GET_ADDRESS_API_KEY: z
    .string()
    .default(process.env['NEXT_PUBLIC_GET_ADDRESS_API_KEY'] ?? 'unknown'),
  GAIA_RISK_ALLOY_JOURNEY_TOKEN: z
    .string()
    .default(process.env['GAIA_RISK_ALLOY_JOURNEY_TOKEN'] ?? 'unknown'),
  GOOGLE_SERVICE_ACCOUNT_KEY: z.string().optional(),
  GOOGLE_SERVICE_ACCOUNT_CREDENTIALS: z.string().optional(),
  PRICING_SPREADSHEET_ID: z.string().optional(),
  CLINIC_PRICING_SPREADSHEET_ID: z.string().optional(),
  FRAPPE_API_SECRET: z.string().optional(),
})

/**
 * Parsed environment
 */
export const env = conf.parse(process.env)
